import api, { getApiErrors } from '@app/services/api';
import { apiEndpoints, defaultErrorMessage } from '@app/utils/constants';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError, AxiosResponse } from 'axios';
import useApp from '../useApp';
import type {
	ApiError,
	ContactAddParams,
	ContactAddResponse,
	ContactEditParams,
	ContactEditResponse,
	ContactDeleteParams,
	ContactDeleteResponse,
	ContactListParams,
} from '@app/services/api.types';
import { fn } from '@app/utils';

export const createContact = async (params: ContactAddParams): Promise<ContactAddResponse> => {
	const url = apiEndpoints.contacts.add;
	const response = await api.post(url, params);
	return response?.data?.data;
};

export const updateContact = async (params: ContactEditParams): Promise<ContactEditResponse> => {
	const url = apiEndpoints.contacts.update(params.id);
	const response = await api.put(url, params);
	return response?.data?.data;
};

export const removeContact = async (
	params: ContactDeleteParams
): Promise<ContactDeleteResponse> => {
	const url = apiEndpoints.contacts.delete(params.id);
	const response = await api.delete(url);
	return response?.data?.data;
};

export const exportContactList = async (params: ContactListParams) => {
	const url = apiEndpoints.contacts.getListExport(params);
	const response = await api.get<Blob>(url, {
		responseType: 'blob',
	});
	return response;
};

const useContactActions = () => {
	const { message } = useApp();

	const addContact = useMutation<ContactAddResponse, AxiosError<ApiError>, ContactAddParams>({
		mutationFn: createContact,
		onSuccess: () => {
			message.success({
				type: 'success',
				content: 'Contact added successfully',
			});
		},
		onError: (error: AxiosError<ApiError>) => {
			const response = error.response?.data;
			const errorMessage = getApiErrors(response?.error, true);
			message.error({
				type: 'error',
				content: errorMessage || defaultErrorMessage,
			});
		},
		meta: {
			invalidates: [['contact', 'list']],
		},
	});

	const editContact = useMutation<ContactEditResponse, AxiosError<ApiError>, ContactEditParams>({
		mutationFn: updateContact,
		onSuccess: () => {
			message.success({
				type: 'success',
				content: 'Contact updated successfully',
			});
		},
		onError: (error: AxiosError<ApiError>) => {
			const response = error.response?.data;
			const errorMessage = getApiErrors(response?.error, true);
			message.error({
				type: 'error',
				content: errorMessage || defaultErrorMessage,
			});
		},
		meta: {
			invalidates: [['contact', 'list']],
		},
	});

	const deleteContact = useMutation<
		ContactDeleteResponse,
		AxiosError<ApiError>,
		ContactDeleteParams
	>({
		mutationFn: removeContact,
		onSuccess: () => {
			message.success({
				type: 'success',
				content: 'Contact deleted successfully',
			});
		},
		onError: (error: AxiosError<ApiError>) => {
			const response = error.response?.data;
			const errorMessage = getApiErrors(response?.error, true);
			message.error({
				type: 'error',
				content: errorMessage || defaultErrorMessage,
			});
		},
		meta: {
			invalidates: [['contact', 'list']],
		},
	});

	const exportContacts = useMutation<
		AxiosResponse<Blob>,
		AxiosError<ApiError>,
		ContactListParams
	>({
		mutationFn: exportContactList,
		onSuccess: (res) => {
			const match = res.headers['content-disposition'].match(/filename\s*=\s*"(.+)"/i);
			const filename = match?.[1] || 'contacts.csv';
			fn.downloadFileFromBlob({
				blob: res.data,
				fileName: filename,
			});
		},
		onError: (error: AxiosError<ApiError>) => {
			const response = error.response?.data;
			const errorMessage = getApiErrors(response?.error, true);
			message.error({
				type: 'error',
				content: errorMessage || defaultErrorMessage,
			});
		},
	});

	return {
		addContact,
		editContact,
		deleteContact,
		exportContacts,
	};
};

export default useContactActions;
